import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { Reveal, ChapterHeader } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUOTES = [
  {
    text: "When we are no longer able to change a situation, we are challenged to change ourselves.",
    author: "Viktor Frankl",
  },
  {
    text: "The curious paradox is that when I accept myself just as I am, then I change.",
    author: "Carl Rogers",
  },
  {
    text: "Feelings come and go like clouds in a windy sky. Conscious breathing is my anchor.",
    author: "Thich Nhat Hanh",
  },
];

export const Reflections = () => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/reflections`)
      .then(({ data }) => setPosts(data))
      .catch(() => setPosts([]));
  }, []);

  return (
    <section id="reflections" data-testid="reflections-section" className="py-24 md:py-32 bg-pawc-sand">
      <div className="max-w-6xl mx-auto px-6">
        <ChapterHeader number="07" eyebrow="Slow Water" title="Notes from beside the river" />

        <Reveal delay={0.1}>
          <p className="max-w-2xl text-pawc-slate text-base md:text-lg leading-relaxed mt-6">
            Short reflections drawing on psychology and philosophy. Thoughts to sit
            with, not instructions to follow.
          </p>
        </Reveal>

        {posts.length > 0 && (
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {posts.map((p, i) => {
              const tall = i === 0;
              const span = tall ? "md:col-span-2 md:row-span-2" : "";
              return (
                <Reveal key={p.id} delay={0.08 * i} className={span}>
                  <Link
                    to={`/reflections/${p.slug}`}
                    data-testid={`reflection-card-${i}`}
                    className={`group h-full rounded-2xl border border-pawc-teal/10 p-8 flex flex-col justify-between hover:-translate-y-1 transition-transform duration-500 ${
                      tall ? "bg-pawc-deep text-pawc-sand" : "bg-pawc-paper"
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-3 mb-5">
                        <span
                          className={`text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full border ${
                            tall
                              ? "border-pawc-sand/30 text-pawc-mist/80"
                              : "border-pawc-teal/25 text-pawc-teal"
                          }`}
                        >
                          {p.tag}
                        </span>
                        <span className={`text-xs ${tall ? "text-pawc-mist/60" : "text-pawc-slate/70"}`}>
                          {p.date}
                        </span>
                      </div>
                      <h3
                        className={`font-serif leading-snug mb-4 ${
                          tall ? "text-3xl md:text-4xl" : "text-2xl text-pawc-deep"
                        }`}
                      >
                        {p.title}
                      </h3>
                      <p
                        className={`text-sm md:text-base leading-relaxed ${
                          tall ? "text-pawc-mist/75" : "text-pawc-slate"
                        }`}
                      >
                        {p.excerpt}
                      </p>
                    </div>
                    <span
                      className={`mt-8 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase ${
                        tall ? "text-pawc-terra" : "text-pawc-teal"
                      }`}
                    >
                      Read reflection <ArrowRight size={14} />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        )}

        <Reveal delay={0.15}>
          <h3
            data-testid="words-to-sit-with"
            className="font-serif italic text-3xl md:text-4xl text-pawc-deep mt-24 mb-10 text-center"
          >
            Words to sit with
          </h3>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {QUOTES.map((q, i) => (
            <Reveal key={q.author} delay={0.1 * i}>
              <figure
                data-testid={`quote-card-${i}`}
                className="h-full rounded-2xl bg-pawc-mist border border-pawc-teal/10 p-8"
              >
                <blockquote className="font-serif italic text-lg md:text-xl text-pawc-deep leading-relaxed">
                  "{q.text}"
                </blockquote>
                <figcaption className="mt-5 text-xs tracking-[0.25em] uppercase text-pawc-teal">
                  {q.author}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
